import { useState } from "react";
import { toast } from "sonner";
import { Send, MessageCircle, Mail } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Reveal } from "./Reveal";

export default function Contact({ settings }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [waMsg, setWaMsg] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Completa todos los campos.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("¡Mensaje enviado! Te responderemos pronto.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const sendWhatsApp = () => {
    const text = waMsg.trim() || "Hola MO by Vir 🌸 Quisiera más información.";
    window.open(
      `https://wa.me/${settings?.whatsapp_number}?text=${encodeURIComponent(text)}`,
      "_blank"
    );
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32"
    >
      <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
        {/* WhatsApp direct */}
        <Reveal>
          <span className="text-[11px] uppercase tracking-[0.3em] text-rose">
            Hablemos
          </span>
          <h2 className="mt-4 font-serif font-light text-4xl md:text-6xl leading-[1.02]">
            Escríbele <span className="italic text-rose">directo</span> a Vir
          </h2>
          <p className="text-taupe mt-5 font-light max-w-md">
            La forma más rápida de resolver dudas, coordinar tu pedido o pedir
            recomendaciones. Te respondemos por WhatsApp.
          </p>

          <div className="mt-8">
            <label className="text-[11px] uppercase tracking-[0.2em] text-taupe">
              Tu mensaje
            </label>
            <textarea
              data-testid="whatsapp-quick-message"
              value={waMsg}
              onChange={(e) => setWaMsg(e.target.value)}
              rows={3}
              placeholder="Hola, me interesa..."
              className="w-full mt-2 bg-transparent border-b border-line py-3 text-espresso placeholder:text-taupe/50 focus:outline-none focus:border-rose transition-colors resize-none font-light"
            />
            <button
              data-testid="whatsapp-quick-send"
              onClick={sendWhatsApp}
              className="mt-6 inline-flex items-center gap-3 bg-rose text-cream rounded-full pl-7 pr-5 py-4 text-xs uppercase tracking-[0.2em] hover:bg-rose-hover transition-colors"
            >
              Enviar por WhatsApp
              <span className="w-8 h-8 rounded-full bg-cream/20 flex items-center justify-center">
                <MessageCircle size={15} />
              </span>
            </button>
          </div>
        </Reveal>

        {/* Traditional email form */}
        <Reveal delay={0.15}>
          <div className="border border-line p-8 md:p-10">
            <div className="flex items-center gap-2 text-taupe">
              <Mail size={16} />
              <span className="text-[11px] uppercase tracking-[0.2em]">
                Formulario de contacto
              </span>
            </div>
            <form onSubmit={submit} className="mt-8 space-y-6">
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-taupe">
                  Nombre
                </label>
                <input
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full mt-2 bg-transparent border-b border-line py-3 focus:outline-none focus:border-rose transition-colors font-light"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-taupe">
                  Correo electrónico
                </label>
                <input
                  data-testid="contact-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full mt-2 bg-transparent border-b border-line py-3 focus:outline-none focus:border-rose transition-colors font-light"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-taupe">
                  Mensaje
                </label>
                <textarea
                  data-testid="contact-message"
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full mt-2 bg-transparent border-b border-line py-3 focus:outline-none focus:border-rose transition-colors resize-none font-light"
                />
              </div>
              <button
                data-testid="contact-submit"
                type="submit"
                disabled={sending}
                className="inline-flex items-center gap-3 bg-espresso text-cream rounded-full pl-7 pr-5 py-4 text-xs uppercase tracking-[0.2em] hover:bg-rose transition-colors disabled:opacity-60"
              >
                {sending ? "Enviando..." : "Enviar mensaje"}
                <span className="w-8 h-8 rounded-full bg-cream/15 flex items-center justify-center">
                  <Send size={14} />
                </span>
              </button>
            </form>
            {settings?.contact_email && (
              <p className="text-xs text-taupe mt-6">
                O escríbenos a{" "}
                <a
                  href={`mailto:${settings.contact_email}`}
                  className="text-rose underline underline-offset-2"
                >
                  {settings.contact_email}
                </a>
              </p>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
